import * as Styled from '../../../../../../components/SearchPage/components/Listings/components/SearchCard/SearchCard.styled';
import type { SearchCardProps } from '../../../../../../components/SearchPage/components/Listings/components/SearchCard/SearchCard.typed';
import { Card } from '../../../../../../components/ToolkitV2/CardV2/Card';
import { ORIENTATION } from '../../../../../../components/ToolkitV2/CardV2/Card.typed';
import { BrandingStrip } from '../../../../../../components/ToolkitV2/CardV2/components/BrandingStrip/BrandingStrip';
import { Highlight } from '../../../../../../components/ToolkitV2/CardV2/components/Highlight/Highlight';
import { Gallery } from '../../../../../../components/AdCard/Gallery/Gallery';
import Price from '../../../../../../components/AdCard/Price/Price';
import { SaveAd } from '../../../../../../components/AdCard/SaveAd/SaveAd';
import { useSaveAd } from '../../../../../../components/Support/UseSaveAd/useSaveAd';
import { WithImageOverlay } from '../../../../../../components/ToolkitV2/CardV2/enhancements/WithImageOverlay/WithImageOverlay';
import { RatingStrip } from '../RatingStrip/RatingStrip';
import { FEATURE_FREE_HISTORY_CHECK } from '../../../../../../utils';
import { useConvertContext } from '../../../../../../contexts/ConvertContext';
import { PAGE } from '../../../../../../helpers/pages';
import { Text } from '../../../../../../components/ToolkitV2/Text/Text';

const SearchCard = (props: SearchCardProps) => {
  const {
    ad,
    isGrid = false,
    currentSection,
    createSavedAdEvent,
    deleteSavedAdEvent,
    isFirst = false,
  } = props;
  const {
    gallery,
    dealer,
    seller,
    relativeUrl,
    friendlyUrl,
    spotlighted,
    title,
    tags,
    tagsV2,
    imageAlt,
    metaInfo,
    age,
    county,
    countyTown,
    section,
  } = ad;

  const { showPricingIndicator } = useConvertContext({ page: PAGE.SEARCH });
  const shouldShowPricingIndicator =
    showPricingIndicator && section.name === 'cars' && Boolean(dealer);

  const displayTags = FEATURE_FREE_HISTORY_CHECK && tagsV2 ? tagsV2 : tags;

  const { previousPrice, price, financePPM, priceOnRequest } = ad.priceInfo;
  const listingUrl = relativeUrl ?? friendlyUrl;

  // TODO: Refactor this to check for a boolean for freeReport instead of inferring from tags
  // Not currently possible due to API response, planned refactor in the new year.
  // Ticket https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=board/4715599953694369603&appConfig=eyJhY2lkIjoiIn0=&boardPopup=userstory/112350/silent

  const isFreeHistoryCheckAd =
    FEATURE_FREE_HISTORY_CHECK &&
    tagsV2?.some((tag) => tag.type === 'freeHistoryReport');

  const orientationConfig = {
    orientationSmall: ORIENTATION.VERTICAL,
    orientationMedium: isGrid ? ORIENTATION.VERTICAL : ORIENTATION.HORIZONTAL,
    orientationLarge: isGrid ? ORIENTATION.VERTICAL : ORIENTATION.HORIZONTAL,
  };

  const getLocationText = () => {
    const locationText =
      countyTown && county ? `${countyTown}, ${county}` : county;
    const countyOrCountyTownText = isGrid && county ? county : locationText;
    return countyOrCountyTownText ? [countyOrCountyTownText] : [];
  };

  const metaData: string[] = [
    ...(!isGrid && metaInfo ? metaInfo : []),
    age,
    ...getLocationText(),
    ...(currentSection !== section.name ? [section.displayName] : []),
  ];

  const { onSave, isAdSaved, isSaving } = useSaveAd({
    adId: ad.id,
    userSaved: ad.saved,
    section: ad.section.name,
    sellerType: dealer ? 'DEALER' : 'PRIVATE',
    sectionName: ad.section.name,
    trackCreateSaveAd: createSavedAdEvent,
    trackDeleteSaveAd: deleteSavedAdEvent,
    isFreeHistoryCheckAd,
    category: ad.section.name,
    location: ad.county,
    price: ad.priceInfo.price,
  });

  return (
    // We chose not to use Next Link here as explained in this PR:
    // https://github.com/distilledmedia/dsch-donedeal-frontend/pull/2183
    <a href={listingUrl}>
      <Styled.Wrapper isGrid={isGrid}>
        {!isGrid && dealer && (
          <BrandingStrip
            {...dealer}
            logo={dealer.logo?.small}
            isGrid={isGrid}
          />
        )}
        {spotlighted && <Highlight color="GREY_DARKER">Spotlight</Highlight>}
        <Card
          {...orientationConfig}
          image={
            <WithImageOverlay
              topRightSlot={
                <Styled.ImageSavedAd isGrid={isGrid}>
                  <SaveAd
                    toggleSave={onSave}
                    isAdSaved={isAdSaved}
                    hideText
                    iconSizeDesktop="MD"
                    isSaving={isSaving}
                  />
                </Styled.ImageSavedAd>
              }
            >
              <Gallery
                coverImage={gallery?.coverImage.small2}
                thumbnails={gallery?.thumbnails}
                totalMediaCount={gallery?.totalMediaCount}
                isSpotlight={ad.spotlighted}
                isGrid={isGrid}
                title={title}
                imageAlt={imageAlt ?? 'no-image'}
              />
            </WithImageOverlay>
          }
          header={
            <Styled.HeaderContainer>
              <Styled.Header isGrid={isGrid}>
                <RatingStrip
                  dealer={dealer || null}
                  seller={seller || null}
                  isGrid={isGrid}
                />
                <Styled.Title
                  lines={2}
                  isGrid={isGrid}
                  fontSizes={{
                    sm: isGrid ? 'M14' : 'L18',
                  }}
                  forwardedAs={'p'}
                >
                  {title}
                </Styled.Title>
                <Styled.MetaInfo lines={2} forwardedAs={'ul'}>
                  {metaData &&
                    metaData.map((info, index) => (
                      <Styled.MetaInfoItem
                        key={index}
                        fontSizes={{
                          sm: isGrid ? 'B14' : 'M14',
                        }}
                      >
                        {info}
                      </Styled.MetaInfoItem>
                    ))}
                </Styled.MetaInfo>
              </Styled.Header>
              {/* 
               Temp to show experiment is working when deployed
               Will be replaced when actual UI is ready 
               */}
              {shouldShowPricingIndicator && isFirst && (
                <Text size="S12">Convert staging exp</Text>
              )}
              {!isGrid && displayTags?.length && (
                <Styled.TagsList
                  singleLine={dealer?.singleLineTag ?? true}
                  tags={displayTags}
                />
              )}
            </Styled.HeaderContainer>
          }
          footer={
            <>
              <Price
                previousPrice={previousPrice}
                price={price}
                financePPM={financePPM}
                priceOnRequest={priceOnRequest}
                isGrid={isGrid}
              />
              {!isGrid && (
                <Styled.BodySavedAd isGrid={isGrid}>
                  <SaveAd
                    toggleSave={onSave}
                    isAdSaved={isAdSaved}
                    hideText
                    iconSizeDesktop="MD"
                    isSaving={isSaving}
                  />
                </Styled.BodySavedAd>
              )}
            </>
          }
          gapSmall={isGrid ? 'S4' : 'M16'}
          gapMedium={isGrid ? 'S8' : undefined}
          paddingSmall={isGrid ? 'S4' : 'M16'}
          paddingMedium={isGrid ? 'S8' : 'M16'}
        />
      </Styled.Wrapper>
    </a>
  );
};

export { SearchCard };
